import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle2, Loader2, MailCheck, XCircle } from "lucide-react";
import { trpc } from "@/trpc/client";
import Config from "@/config/config";
import { resolveApproveDestination } from "@/lib/approveDestination";
import { getSafeRedirectUrl } from "@/lib/safeRedirect";
import { AuxPage } from "@/components/shared/AuxPage";
import { auxControlClass } from "@/components/shared/auxControl";

export const Route = createFileRoute("/_home/approve/$hash")({
  validateSearch: (search: Record<string, unknown>) => ({
    redirect_url:
      typeof search.redirect_url === "string" ? search.redirect_url : "",
    // Set by the approve-link route once it has recorded the decision, so this
    // page can tell "just came from the email link" apart from "revisiting an
    // old link". Only the former continues into the app.
    //
    // The router's default search serializer is JSON-based, so `true` survives a
    // navigation and a reload as the boolean `true` (verified against
    // `defaultStringifySearch`/`defaultParseSearch`). The string forms are
    // defensive: they keep the marker readable if the URL is ever hand-built or
    // a serializer is swapped in that emits `"true"`/`"1"`.
    just_approved:
      search.just_approved === true ||
      search.just_approved === "true" ||
      search.just_approved === "1",
  }),
  component: ApprovePage,
});

function ApprovePage() {
  const { hash } = Route.useParams();
  const { redirect_url: rawRedirectUrl, just_approved: cameFromEmailLink } =
    Route.useSearch();

  const info = useQuery(
    trpc.publicRouterFunction.getApprovalInfo.queryOptions({ hash }),
  );

  const approveMutation = useMutation(
    trpc.publicRouterFunction.approveEmail.mutationOptions(),
  );

  const rejectMutation = useMutation(
    trpc.publicRouterFunction.rejectEmail.mutationOptions(),
  );

  const isPending = approveMutation.isPending || rejectMutation.isPending;

  function handleApprove() {
    rejectMutation.reset();
    approveMutation.mutate({ hash });
  }

  function handleReject() {
    approveMutation.reset();
    rejectMutation.mutate({ hash });
  }

  // A fresh approval is either one this page just performed (the "review
  // first" link) or one the approve-link route recorded before sending us here.
  const justApproved = cameFromEmailLink || approveMutation.isSuccess;
  const isGranted =
    approveMutation.isSuccess || info.data?.consentStatus === "granted";
  const safeRedirectUrl = getSafeRedirectUrl(rawRedirectUrl);
  // Only ever `external` (a sender-configured redirect) or `stay`. The
  // GoodSender signup email carries no redirect_url, so it always stays on the
  // confirmation panel — no auto-navigation into the auth-gated dashboard,
  // which bounced logged-in users to /login (see approveDestination.ts).
  const destination = isGranted
    ? resolveApproveDestination({ safeRedirectUrl, justApproved })
    : null;
  const leavingTo = destination?.kind === "external" ? destination.url : null;

  useEffect(() => {
    if (leavingTo) {
      // `replace`, not `href`: this route is a transient bounce, so we must not
      // leave the "Redirecting" page in history — otherwise Back lands the user
      // on a dead-end redirect screen. Matches the approve-link route.
      window.location.replace(leavingTo);
    }
  }, [leavingTo]);

  if (leavingTo) {
    return (
      <AuxPage
        icon={<Loader2 className="size-8 animate-spin" aria-hidden="true" />}
        title="Redirecting"
        // Reachable only when `isGranted` — `destination` is null otherwise —
        // so this cannot mislabel a rejection.
        status="Consent granted"
      >
        <p className={BODY_CLASS}>
          Taking you back to {senderName(info.data?.workspaceName, "the sender")}
          &apos;s website.
        </p>
      </AuxPage>
    );
  }

  if (approveMutation.isSuccess) {
    const { email, workspaceName } = approveMutation.data;

    return (
      <StatusResult
        variant="granted"
        email={email}
        workspaceName={workspaceName}
        description="can now send emails to this address."
        onReverse={handleReject}
        reverseLabel={rejectMutation.isPending ? "Rejecting…" : "reject"}
        isPending={isPending}
      />
    );
  }

  if (rejectMutation.isSuccess) {
    const { email, workspaceName } = rejectMutation.data;
    return (
      <StatusResult
        variant="denied"
        email={email}
        workspaceName={workspaceName}
        description="will not be able to send emails to this address."
        onReverse={handleApprove}
        reverseLabel={
          approveMutation.isPending ? "Approving…" : "approve"
        }
        isPending={isPending}
        redirectNote={
          safeRedirectUrl
            ? `After approving, you will be redirected to ${senderName(workspaceName, "the sender")}'s website.`
            : undefined
        }
      />
    );
  }

  if (approveMutation.isError || rejectMutation.isError || info.isError) {
    return (
      <AuxPage
        icon={
          <XCircle
            className="size-8 text-[color:var(--gs-danger)]"
            aria-hidden="true"
          />
        }
        title="Link not found"
        status="Link unavailable"
      >
        <p className={BODY_CLASS}>
          This approval link is invalid or has already been used.
        </p>
      </AuxPage>
    );
  }

  if (info.isLoading) {
    return (
      <AuxPage
        icon={<Loader2 className="size-8 animate-spin" aria-hidden="true" />}
        title="Loading"
        status="Checking your email preferences"
      >
        <p className={BODY_CLASS}>Fetching approval info…</p>
      </AuxPage>
    );
  }

  const workspaceName = info.data?.workspaceName;
  const consentStatus = info.data?.consentStatus;

  if (consentStatus === "granted") {
    return (
      <StatusResult
        variant="granted"
        email={info.data?.email}
        workspaceName={workspaceName}
        description="is allowed to send emails to this address."
        onReverse={handleReject}
        reverseLabel={rejectMutation.isPending ? "Rejecting…" : "reject"}
        isPending={isPending}
      />
    );
  }

  if (consentStatus === "denied") {
    return (
      <StatusResult
        variant="denied"
        email={info.data?.email}
        workspaceName={workspaceName}
        description="has been blocked from sending emails to this address."
        onReverse={handleApprove}
        reverseLabel={
          approveMutation.isPending ? "Approving…" : "approve"
        }
        isPending={isPending}
        redirectNote={
          safeRedirectUrl
            ? `After approving, you will be redirected to ${senderName(workspaceName, "the sender")}'s website.`
            : undefined
        }
      />
    );
  }

  return (
    <AuxPage
      icon={
        <MailCheck
          className="size-8 text-[color:var(--gs-muted)]"
          aria-hidden="true"
        />
      }
      title="Allow email delivery"
      status="Your inbox, your choice"
    >
      <p className={BODY_CLASS}>
        <SenderName name={workspaceName} fallback="An organization" /> is
        requesting permission to send emails to your address.
      </p>
      {info.data?.email && <p className={EMAIL_CLASS}>{info.data.email}</p>}
      {/*
        The operative disclosure — the sentence that states what the recipient is
        granting. It keeps body size, its own paragraph, and the emphasized
        sender; only the cooldown microcopy below it is fine print. The first cut
        of this migration folded the two together at `text-xs`, which shrank the
        one line the page exists to make (#868 review).
      */}
      <p className={DISCLOSURE_CLASS}>
        By approving, you allow{" "}
        <SenderName name={workspaceName} fallback="this organization" /> to send
        you emails in the future.
      </p>
      <p className={FINE_PRINT_CLASS}>
        Rejecting permanently blocks this sender. Ignoring this request allows
        them to ask again after {Config.approvalCooldownDays} days.
      </p>
      {/* Gate the note on the SAFE url, never the raw param: an http: target is
          intentionally refused at redirect time, so promising a redirect here
          would lie to the recipient. Ported from the production hotfix. */}
      {safeRedirectUrl && (
        <p className={FINE_PRINT_CLASS}>
          After approving, you will be redirected to{" "}
          {senderName(workspaceName, "the sender")}&apos;s website.
        </p>
      )}
      <div className="mt-8 flex flex-col gap-3">
        <button
          type="button"
          onClick={handleApprove}
          disabled={isPending}
          className={auxControlClass("primary")}
        >
          {approveMutation.isPending ? (
            <span>Approving…</span>
          ) : (
            "Approve"
          )}
        </button>
        <button
          type="button"
          onClick={handleReject}
          disabled={isPending}
          className={auxControlClass("secondary")}
        >
          {rejectMutation.isPending ? (
            <span>Rejecting…</span>
          ) : (
            "Reject"
          )}
        </button>
      </div>
    </AuxPage>
  );
}

// Keep recipient details and secondary copy on the shared type scale.
const BODY_CLASS =
  "text-[length:var(--gs-text-body)] leading-relaxed text-[color:var(--gs-text)]";
const EMAIL_CLASS =
  "mt-4 rounded-[var(--gs-radius-sm)] bg-[color:var(--gs-bg)] px-3 py-2.5 text-[length:var(--gs-text-body)] font-medium break-all text-[color:var(--gs-text)]";
const DISCLOSURE_CLASS =
  "mt-6 text-[length:var(--gs-text-body)] leading-relaxed text-[color:var(--gs-muted)]";
const FINE_PRINT_CLASS =
  "mt-3 text-[length:var(--gs-text-meta)] leading-relaxed text-[color:var(--gs-muted)]";

/**
 * `workspaceName` reaches this page through a `leftJoin`
 * (`functions/src/email/db.ts`), so it is nullable — and nothing stops a
 * workspace being saved with a blank name, which `??` would let straight into
 * the middle of a sentence ("you allow  to send you emails"). One blankness
 * rule, used by every call site on the page (#868 review).
 */
function senderName(name: string | null | undefined, fallback: string): string {
  return name?.trim() ? name : fallback;
}

/** The same rule where the resolved name is emphasized rather than inlined. */
function SenderName({
  name,
  fallback,
}: {
  name: string | null | undefined;
  fallback: string;
}) {
  return name?.trim() ? (
    <span className="font-medium">{name}</span>
  ) : (
    <>{fallback}</>
  );
}

const statusConfig = {
  granted: {
    Icon: CheckCircle2,
    iconClass: "size-8 text-[color:var(--gs-ok)]",
    title: "Emails approved",
    status: "Consent granted",
  },
  denied: {
    Icon: XCircle,
    iconClass: "size-8 text-[color:var(--gs-danger)]",
    title: "Emails denied",
    status: "Consent denied",
  },
};

/**
 * Both result states, for both routes into them — a decision this page just
 * recorded, and a revisit of an old link that reads the stored one.
 *
 * The title lives in `statusConfig`, not at the four call sites, because that is
 * how the four came to disagree in pairs while describing identical state:
 * "Consent granted"/"Granted" and "Consent denied"/"All emails denied". Plural
 * throughout — approving grants standing consent, not approval of one message.
 */
function StatusResult({
  variant,
  email,
  workspaceName,
  description,
  onReverse,
  reverseLabel,
  isPending,
  redirectNote,
}: {
  variant: "granted" | "denied";
  email?: string;
  workspaceName?: string | null;
  description: string;
  onReverse: () => void;
  reverseLabel: string;
  isPending: boolean;
  redirectNote?: string;
}) {
  const cfg = statusConfig[variant];
  const Icon = cfg.Icon;
  return (
    <AuxPage
      icon={<Icon className={cfg.iconClass} aria-hidden="true" />}
      title={cfg.title}
      status={cfg.status}
    >
      <p className={BODY_CLASS}>
        <SenderName name={workspaceName} fallback="This organization" />{" "}
        {description}
      </p>
      {email && <p className={EMAIL_CLASS}>{email}</p>}
      <p
        className={`${FINE_PRINT_CLASS} mt-6 border-t border-[color:var(--gs-border)] pt-5`}
      >
        Changed your mind? You can{" "}
        <button
          type="button"
          onClick={onReverse}
          disabled={isPending}
          className="inline border-0 bg-transparent p-0 font-medium text-[color:var(--gs-accent)] underline underline-offset-4 hover:text-[color:var(--gs-accent-strong)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--gs-accent)] disabled:cursor-not-allowed disabled:no-underline disabled:opacity-50"
        >
          {reverseLabel}
        </button>{" "}
        this sender instead.
      </p>
      {redirectNote && <p className={FINE_PRINT_CLASS}>{redirectNote}</p>}
    </AuxPage>
  );
}
